import type { Scte35Marker } from "../types";
import type {
  NormalizedScte35Cue,
  Scte35CueContext,
  Scte35CueSource,
  Scte35CueReadiness,
} from "./playerTypes";
import { getFragmentStartSeconds } from "./Scte35Parser";

export interface Scte35CueManagerCallbacks {
  onEarlyStartCue(marker: Scte35Marker, fragmentSn: number | undefined, cueKey: string): void;
  onPlaybackCue(marker: Scte35Marker, cueKey?: string): void;
  isInAdBreak(): boolean;
}

export class Scte35CueManager {
  private readonly pendingScte35Cues: Map<string, NormalizedScte35Cue> = new Map();
  private readonly recentScte35CueKeys: Map<string, number> = new Map();
  private readonly recentScte35CueLimit: number = 200;
  private pendingScte35CueKeyValue: string | undefined;
  private activeScte35BreakKeyValue: string | undefined;
  private scheduledScte35BreakKeyValue: string | undefined;

  readonly tsScte35Pids: Set<number> = new Set();
  readonly pmtPids: Set<number> = new Set();
  readonly processedTsScte35Sections: Set<string> = new Set();

  private debug: boolean;
  private callbacks: Scte35CueManagerCallbacks;

  constructor(debug: boolean, callbacks: Scte35CueManagerCallbacks) {
    this.debug = debug;
    this.callbacks = callbacks;
  }

  get pendingScte35CueKey(): string | undefined {
    return this.pendingScte35CueKeyValue;
  }
  set pendingScte35CueKey(value: string | undefined) {
    this.pendingScte35CueKeyValue = value;
  }

  get activeScte35BreakKey(): string | undefined {
    return this.activeScte35BreakKeyValue;
  }
  set activeScte35BreakKey(value: string | undefined) {
    this.activeScte35BreakKeyValue = value;
  }

  get scheduledScte35BreakKey(): string | undefined {
    return this.scheduledScte35BreakKeyValue;
  }
  set scheduledScte35BreakKey(value: string | undefined) {
    this.scheduledScte35BreakKeyValue = value;
  }

  createScte35CueContext(
    source: Scte35CueSource,
    readiness: Scte35CueReadiness,
    frag?: any
  ): Scte35CueContext {
    const context: Scte35CueContext = { source, readiness };
    if (typeof frag?.sn === "number") {
      context.fragmentSn = frag.sn;
    }
    const fragmentStartSec = getFragmentStartSeconds(frag);
    if (fragmentStartSec !== undefined) {
      context.fragmentStartSeconds = fragmentStartSec;
    }
    return context;
  }

  onScte35Cue(marker: Scte35Marker, context: Scte35CueContext): void {
    const cue = this.createScte35Cue(marker, context);
    const pendingKey = this.getPendingScte35CueKey(cue);

    if (context.readiness === "early") {
      this.pendingScte35Cues.set(pendingKey, cue);
      this.trimRecentScte35CueMaps();

      if (marker.type === "start") {
        this.callbacks.onEarlyStartCue(marker, context.fragmentSn, cue.key);
      }
      return;
    }

    if (marker.type === "end" && !this.shouldAcceptScte35EndCue(cue)) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Ignoring SCTE-35 end cue", {
          key: cue.key,
          activeScte35BreakKey: this.activeScte35BreakKeyValue,
          inAdBreak: this.callbacks.isInAdBreak(),
          source: cue.source,
        });
      }
      return;
    }

    if (marker.type === "start" && this.hasRecentlyHandledScte35Cue(cue)) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Ignoring duplicate SCTE-35 start cue", {
          key: cue.key,
          source: cue.source,
        });
      }
      return;
    }

    this.pendingScte35Cues.delete(pendingKey);
    this.markScte35CueHandled(cue);
    this.callbacks.onPlaybackCue(marker, cue.key);
  }

  activatePendingScte35CuesForFragment(frag: any): void {
    const fragmentSn = typeof frag?.sn === "number" ? frag.sn : undefined;
    if (fragmentSn === undefined) {
      return;
    }

    const matchingCues = Array.from(this.pendingScte35Cues.entries()).filter(
      ([, cue]) => cue.fragmentSn === fragmentSn
    );

    matchingCues.forEach(([pendingKey, cue]) => {
      this.pendingScte35Cues.delete(pendingKey);
      const context: Scte35CueContext = {
        source: cue.source,
        readiness: "playback",
      };
      context.fragmentSn = fragmentSn;
      const fragmentStartSec =
        cue.fragmentStartSeconds ?? getFragmentStartSeconds(frag);
      if (fragmentStartSec !== undefined) {
        context.fragmentStartSeconds = fragmentStartSec;
      }
      this.onScte35Cue(cue.marker, context);
    });
  }

  private createScte35Cue(
    marker: Scte35Marker,
    context: Scte35CueContext
  ): NormalizedScte35Cue {
    const cue: NormalizedScte35Cue = {
      marker,
      source: context.source,
      readiness: context.readiness,
      key: this.getScte35CueKey(marker, context),
    };
    if (context.fragmentSn !== undefined) {
      cue.fragmentSn = context.fragmentSn;
    }
    if (context.fragmentStartSeconds !== undefined) {
      cue.fragmentStartSeconds = context.fragmentStartSeconds;
    }
    return cue;
  }

  private getScte35CueKey(
    marker: Scte35Marker,
    context: Scte35CueContext
  ): string {
    const raw = this.getScte35MarkerRaw(marker);
    const spliceEventId = this.toIdentityString(raw?.splice_event_id);
    if (spliceEventId) {
      return `splice:${spliceEventId}`;
    }

    const segmentationEventId = this.toIdentityString(raw?.segmentation_event_id);
    if (segmentationEventId) {
      return `segmentation:${segmentationEventId}`;
    }

    const daterangeId = this.toIdentityString(raw?.attrs?.ID);
    if (daterangeId) {
      return `daterange:${daterangeId}`;
    }

    const scteValue =
      this.toIdentityString(raw?.attrs?.["SCTE35-OUT"]) ||
      this.toIdentityString(raw?.attrs?.["SCTE35-IN"]);
    if (scteValue) {
      return `daterange-scte:${scteValue}`;
    }

    if (typeof marker.ptsSeconds === "number" && Number.isFinite(marker.ptsSeconds)) {
      return `pts:${Math.round(marker.ptsSeconds * 2) / 2}`;
    }

    if (context.fragmentSn !== undefined) {
      return `fragment:${context.fragmentSn}`;
    }

    return `${context.source}:wallclock:${Math.floor(Date.now() / 1000)}`;
  }

  private getPendingScte35CueKey(cue: NormalizedScte35Cue): string {
    return `${cue.marker.type}:${cue.key}:${
      cue.fragmentSn !== undefined ? cue.fragmentSn : "unknown"
    }`;
  }

  private getScte35MarkerRaw(marker: Scte35Marker): any {
    return typeof marker.raw === "object" && marker.raw !== null
      ? (marker.raw as any)
      : {};
  }

  private toIdentityString(value: unknown): string | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
    return undefined;
  }

  isStrongScte35CueKey(key: string | undefined): boolean {
    return !!(
      key &&
      (key.startsWith("splice:") ||
        key.startsWith("segmentation:") ||
        key.startsWith("daterange:") ||
        key.startsWith("daterange-scte:"))
    );
  }

  private hasRecentlyHandledScte35Cue(cue: NormalizedScte35Cue): boolean {
    if (
      this.activeScte35BreakKeyValue === cue.key ||
      this.scheduledScte35BreakKeyValue === cue.key
    ) {
      return true;
    }

    const recentKey = `${cue.marker.type}:${cue.key}`;
    return this.recentScte35CueKeys.has(recentKey);
  }

  private markScte35CueHandled(cue: NormalizedScte35Cue): void {
    this.recentScte35CueKeys.set(`${cue.marker.type}:${cue.key}`, Date.now());
    this.trimRecentScte35CueMaps();
  }

  private trimRecentScte35CueMaps(): void {
    while (this.recentScte35CueKeys.size > this.recentScte35CueLimit) {
      const firstKey = this.recentScte35CueKeys.keys().next().value;
      if (!firstKey) {
        break;
      }
      this.recentScte35CueKeys.delete(firstKey);
    }

    while (this.pendingScte35Cues.size > this.recentScte35CueLimit) {
      const firstKey = this.pendingScte35Cues.keys().next().value;
      if (!firstKey) {
        break;
      }
      this.pendingScte35Cues.delete(firstKey);
    }
  }

  private shouldAcceptScte35EndCue(cue: NormalizedScte35Cue): boolean {
    if (!this.callbacks.isInAdBreak()) {
      return false;
    }

    if (
      this.isStrongScte35CueKey(this.activeScte35BreakKeyValue) &&
      this.isStrongScte35CueKey(cue.key) &&
      this.activeScte35BreakKeyValue !== cue.key
    ) {
      return false;
    }

    return true;
  }
}
